import { DeleteOutline } from '@mui/icons-material';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { TRACE_TYPES_WITH_EXCEPTIONS, getTraceTypeLabel } from '../../../common/enums/TraceType';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { bind } from '../../../common/utils/decorators';
import { SuccessNotification } from '../../../common/utils/notifications/Notification';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { ProjectExceptionType, getExceptionTypeLabel } from '../../../project-exceptions/enums/ProjectExceptionType';
import { ProjectException } from '../../../project-exceptions/models/ProjectException';
import { ProjectExceptionRepository } from '../../../project-exceptions/services/ProjectExceptionRepository';
import { ProjectOutletContextType } from '../../common/contexts/ProjectOutletContext';
import { BlankState } from '../components/BlankState';
import { DocsAlert } from '../../../common/docs/DocsAlert';
import { ExceptionStatusToggle } from '../components/ExceptionStatusToggle';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { EXTERNAL_URLS } from '../../../routing/routes';

type Props = WithRouterProps<ProjectOutletContextType> & {};

type State = PaginatedTableState & {
  canUpdateProject: boolean;
};

class Controller extends PaginatedTableController<ProjectException, Props, State> {
  private readonly exceptionsRepository = new ProjectExceptionRepository();
  private readonly modalManager = new ModalManager();

  private projectId: number;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ projectId: this.projectId } = this.parseNumericIds(['projectId']));
  }

  getBaseState(): State {
    return {
      ...super.getBaseState(),
      canUpdateProject: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.loadPermissions();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.exceptionsRepository.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // do nothing until permissions are fetched
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<ProjectException>> {
    try {
      return await this.exceptionsRepository.getProjectExceptions(this.projectId, cursorInfo, this.getFilters(), this.getSorters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected renderTable(tableComponent: React.ReactElement) {
    const { loadingData, initialDataLoad, hadAnyData, canUpdateProject } = this.state;
    const isLoading = loadingData || initialDataLoad;
    const activeFilters = this.filters !== null;

    if (!isLoading && !hadAnyData && !activeFilters) {
      return <BlankState onAddException={canUpdateProject ? this.handleCreateException : undefined} />;
    }

    return (
      <Stack gap={2}>
        {!initialDataLoad && <DocsAlert helpText="exceptionsGuidance" href={UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_EXCEPTIONS)} />}
        {tableComponent}
      </Stack>
    );
  }

  protected renderRightToolbarElements(): ReactElement | null {
    const { canUpdateProject } = this.state;

    if (!canUpdateProject) {
      return null;
    }

    return (
      <Button onClick={this.handleCreateException} variant="contained" size="small" color="primary">
        {t('addException')}
      </Button>
    );
  }

  protected getFieldConfigurations(): FieldConfiguration<ProjectException>[] {
    const { canUpdateProject } = this.state;
    const fields: FieldConfiguration<ProjectException>[] = [
      {
        fieldKey: 'traceType',
        label: t('traceType.label'),
        description: t('traceType.label'),
        sortable: true,
        gqlKeys: { sorterField: 'traceType' },
        columnProps: { maxWidth: 200 },
        render: (item) => getTraceTypeLabel(item.getTraceType()),
      },
      {
        fieldKey: 'type',
        label: t('type'),
        description: t('type'),
        sortable: true,
        gqlKeys: { sorterField: 'type' },
        columnProps: { maxWidth: 150 },
        render: (item) => <Chip label={getExceptionTypeLabel(item.getType())} />,
      },
      {
        fieldKey: 'term',
        label: t('term'),
        description: t('term'),
        sortable: true,
        gqlKeys: { sorterField: 'term' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getTerm()}</TypographyOverflow>,
      },
      {
        fieldKey: 'description',
        label: t('description'),
        description: t('description'),
        sortable: true,
        gqlKeys: { sorterField: 'description' },
        render: (item, { hasFocus }) => {
          const description = item.getDescription();
          if (!description) {
            return <BlankCell blankText={t('notSet')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{description}</TypographyOverflow>;
        },
      },
      {
        fieldKey: 'active',
        label: t('status'),
        description: t('status'),
        sortable: true,
        gqlKeys: { sorterField: 'active' },
        columnProps: { maxWidth: 175 },
        render: (item, { tabIndex }) => (
          <ExceptionStatusToggle tabIndex={tabIndex} disabled={!canUpdateProject} value={item.isActive()} onToggle={() => this.handleToggleException(item)} />
        ),
      },
    ];

    if (canUpdateProject) {
      fields.push({
        fieldKey: 'actions',
        label: '',
        description: t('a11y.exceptions.actions'),
        columnProps: { maxWidth: 60, minWidth: 60 },
        render: (item, { tabIndex }) => (
          <Tooltip title={t('a11y.exceptions.actions')}>
            <IconButton tabIndex={tabIndex} onClick={() => this.handleDeleteException(item)}>
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    return fields;
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'traceType',
        label: t('traceType.label'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: TRACE_TYPES_WITH_EXCEPTIONS.map((t) => ({ value: t, label: getTraceTypeLabel(t) })),
      },
      {
        filterKey: 'type',
        label: t('type'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: Object.values(ProjectExceptionType).map((t) => ({ value: t, label: getExceptionTypeLabel(t) })),
      },
      {
        filterKey: 'term',
        label: t('term'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'description',
        label: t('description'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'active',
        label: t('status'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: 'TRUE', label: t('enabled') },
          { value: 'FALSE', label: t('disabled') },
        ],
      },
    ];
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<ProjectException>> {
    return {
      ...super.getAdditionalDataGridProps(),
      getRowId: this.getRowId,
      sx: { '& .MuiDataGrid-columnHeader[data-field="active"]': { pl: 2.5 } },
    };
  }

  private async loadPermissions() {
    try {
      const canUpdate = await this.permissionsService.canUpdateProject(this.projectId);
      this.setState({ canUpdateProject: canUpdate }, () => super.doInitialDataFetch());
    } catch (e: unknown) {
      this.handleError(e, undefined, true);
      // Handle the error silently and still load the table
      super.doInitialDataFetch();
    }
  }

  @bind
  private async handleCreateException() {
    const { exceptionId } = await this.modalManager.createException(this.projectId);

    if (!exceptionId) return;

    this.reloadTableContent();
  }

  @bind
  private async handleToggleException(e: ProjectException) {
    try {
      this.setState({ loadingData: true });
      const updated = await this.exceptionsRepository.toggleException(e.getProjectId(), e.getId(), !e.isActive());

      this.showMessage(new SuccessNotification(t(updated.isActive() ? 'toggleExceptions.enabled' : 'toggleExceptions.disabled')));

      this.reloadTableContent();
    } catch (e: unknown) {
      this.setState({ loadingData: false });
      this.handleError(e);
    }
  }

  @bind
  private async handleDeleteException(e: ProjectException) {
    const { exceptionId } = await this.modalManager.deleteException(e.getId());

    if (!exceptionId) return;

    this.reloadTableContent();
  }

  @bind
  private getRowId(e: ProjectException) {
    // The only mutable field in the table display is the `active` property, rows should refresh on change
    return `${e.getId()}_${e.isActive() ? 'enabled' : 'disabled'}`;
  }
}

export const ProjectExceptionsController = withRouter<ProjectOutletContextType>(Controller);
